import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function ExteriorDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Exterior Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Camarillo', 'Thousand Oaks', 'Ventura County', 'Los Angeles'],
    description: 'Professional mobile exterior car detailing services including hand wash, clay bar treatment, wheel cleaning, and wax protection.'
  };
  const faqs = [{
    question: 'What is included in exterior detailing?',
    answer: 'Our exterior detail includes a safe hand wash, deep wheel and tire cleaning, clay bar decontamination, bug and tar removal, premium wax or sealant application, and tire and trim dressing.'
  }, {
    question: 'Will exterior detailing remove swirl marks?',
    answer: 'A standard exterior detail cleans and protects but does not remove swirl marks. For deeper gloss and lasting protection, pair it with our ceramic coating service.'
  }, {
    question: 'How long does exterior protection last?',
    answer: 'Our hybrid ceramic wax and polymer sealants typically provide several months of protection and water beading, depending on conditions and how often the vehicle is washed.'
  }, {
    question: 'How much does exterior detailing cost?',
    answer: 'Exterior detailing starts at $160, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you bring your own water?',
    answer: 'Yes. We bring our own spot-free water and detailing equipment. We require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/#services'
  }, {
    name: 'Exterior Detailing',
    item: 'https://www.fulldetailpro.com/services/exterior-detailing'
  }];
  return <ServicePageLayout title="Exterior Detailing Ventura County" subtitle="Protect your paint and restore a brilliant shine with our premium mobile exterior detailing services. We bring the ultimate wash and wax directly to you." metaDescription="Top-rated mobile exterior detailing in Ventura County \u2014 Camarillo, Oxnard & Ventura. Hand wash, clay bar & ceramic wax protection. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-789" data-mp="28c9bdv24dkjl">
      <p data-id="element-790" data-mp="1klls6dmr78hq">
        Your vehicle's exterior faces a constant barrage of environmental
        hazards: UV rays, bird droppings, tree sap, road tar, and industrial
        fallout. Over time, these contaminants degrade your clear coat, leaving
        your paint looking dull and feeling rough. At Mike's Pro Detail, our{' '}
        <strong data-id="element-791" data-mp="3azvzutov7dcr">mobile exterior car detailing</strong> service is designed to
        safely remove these contaminants, restore a deep gloss, and apply
        durable protection. Whether you need car detailing in Camarillo,
        Thousand Oaks, or anywhere in Ventura County, our mobile unit provides a
        superior alternative to automated car washes that scratch and swirl your
        paint.
      </p>

      <h2 data-id="element-792" data-mp="1ahow76ihipul">Our Exterior Detailing Process</h2>
      <p data-id="element-793" data-mp="ds7eft689dcj">
        We utilize safe, scratch-free wash methods and premium products to
        ensure your vehicle looks its absolute best. Our exterior detailing
        package includes:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-794" data-mp="msvtmr7cxjr0">
        <li className="flex items-start" data-id="element-795" data-mp="mr2nre4kj3k2">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-796" data-mp="rrgvev29cbhk" />
          <span data-id="element-797" data-mp="rpm85a3ik7ab">
            A meticulous hand wash using premium microfiber towels and
            pH-neutral soap to safely lift dirt without inducing swirl marks.
          </span>
        </li>
        <li className="flex items-start" data-id="element-798" data-mp="23517uln3xd94">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-799" data-mp="21p6tuypeyadv" />
          <span data-id="element-800" data-mp="21rjks1d5fv32">
            <strong data-id="element-801" data-mp="1wqvjmmv7hxv4">Deep Wheel & Tire Cleaning:</strong> We use specialized
            brushes and non-acidic cleaners to remove baked-on brake dust from
            wheel barrels, faces, and tires.
          </span>
        </li>
        <li className="flex items-start" data-id="element-802" data-mp="26nqij6ve609x">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-803" data-mp="26qw9ysrhte8t" />
          <span data-id="element-804" data-mp="2kn9nxrwtixij">
            <strong data-id="element-805" data-mp="2kmq94k2gsp8q">Clay Bar Treatment:</strong> A crucial step that removes
            embedded contaminants (like overspray and rail dust) that washing
            alone cannot remove, leaving the paint smooth as glass.
          </span>
        </li>
        <li className="flex items-start" data-id="element-806" data-mp="2perhhuwihvth">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-807" data-mp="2pg2u8k7xqdwq" />
          <span data-id="element-808" data-mp="33g3vi0s43ncq">
            <strong data-id="element-809" data-mp="33flp4bbqjsr3">Bug & Tar Removal:</strong> Safe breakdown and removal of
            stubborn bug splatter and road tar from the front bumper and lower
            rocker panels.
          </span>
        </li>
        <li className="flex items-start" data-id="element-810" data-mp="3i2kdfxfaq71l">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-811" data-mp="3n0m0etfueqic" />
          <span data-id="element-812" data-mp="3myhvq9a4dnh0">
            <strong data-id="element-813" data-mp="3912zaklg8ffj">Premium Wax / Sealant Application:</strong> Application of a
            high-quality hybrid ceramic wax or polymer sealant to provide months
            of protection and intense water beading.
          </span>
        </li>
        <li className="flex items-start" data-id="element-814" data-mp="2zahuugj3ggop">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-815" data-mp="2z9ghg5elvi3t" />
          <span data-id="element-816" data-mp="343kth32qagbr">
            <strong data-id="element-817" data-mp="3442vyhfzhqs4">Tire & Trim Dressing:</strong> Application of a no-sling,
            UV-blocking dressing to tires and faded exterior plastics to restore
            a rich, dark appearance.
          </span>
        </li>
      </ul>

      <h2 data-id="element-818" data-mp="2gbvsyzfr6ljq">Why Exterior Detailing is Essential</h2>
      <p data-id="element-819" data-mp="2gd76vs2wx9ok">
        Regular exterior detailing is preventative maintenance for your
        vehicle's paint. The Southern California sun can quickly cause clear
        coat failure and oxidation if the paint is left unprotected. By
        regularly removing bonded contaminants through a clay bar treatment and
        applying a protective wax or sealant, you create a sacrificial barrier
        against the elements. This not only keeps your car looking stunning but
        also preserves its resale value and prevents costly paint repairs down
        the line.
      </p>

      <h2 data-id="element-820" data-mp="19suqs8qaftn2">The Premier Choice for Mobile Auto Detailing Camarillo CA</h2>
      <p data-id="element-821" data-mp="14uji41bqbp6f">
        When you search for "Mobile Detailing Ventura County" or need reliable
        service in Thousand Oaks, Mike's Pro Detail is the trusted choice. We
        bring our own spot-free water and detailing equipment, and we require
        access to a standard electrical outlet at the service location, meaning
        we can perform a flawless exterior detail in your driveway, at your
        office parking lot, or even at your apartment complex. We treat every vehicle as if it were
        our own, ensuring that every inch of your exterior receives the
        attention it deserves.
      </p>

      <h2 data-id="element-822" data-mp="212pi3bcc7cz8">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-823" data-mp="20zjszw3pkb3i">
        <Link to="/services/ceramic-coating" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-824" data-mp="251okopalixni">
          Ceramic Coating &rarr;
        </Link>
        <Link to="/services/full-car-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-825" data-mp="2jrvukgmjf8cl">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-826" data-mp="2ssaizoust3kb">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}